import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/services/api";
import type {
  Page,
  SupportRequestAdminItem,
  SupportRequestResolution,
  SupportRequestSeverity,
  SupportRequestStatus,
  SupportSettings,
  SupportSettingsUpdate,
} from "@/types";

/**
 * React Query hooks for the admin Support feature (support requests list +
 * status/admin-notes updates). Wraps the existing API client methods — the
 * client is unchanged — and owns the query keys, caching, and
 * invalidation-on-success that previously lived as a manual `useEffect` loader +
 * manual refetch in the page. List params are baked into the key, so changing
 * page/search/sort/status refetches naturally. Follows the Sources slice
 * template.
 */

export interface SupportListParams {
  page: number;
  page_size: number;
  sort_by: string;
  sort_order: "asc" | "desc";
  q?: string;
  status?: SupportRequestStatus;
  severity?: SupportRequestSeverity;
  unowned?: boolean;
}

export const supportKeys = {
  all: ["admin", "support"] as const,
  lists: () => [...supportKeys.all, "list"] as const,
  list: (params: SupportListParams) => [...supportKeys.lists(), params] as const,
  detail: (id: string) => [...supportKeys.all, "detail", id] as const,
};

export function useSupportRequests(params: SupportListParams) {
  return useQuery<Page<SupportRequestAdminItem>>({
    queryKey: supportKeys.list(params),
    queryFn: () => api.listAdminSupportRequests(params),
  });
}

export function useSupportRequest(id: string | undefined) {
  return useQuery<SupportRequestAdminItem>({
    queryKey: supportKeys.detail(id ?? ""),
    queryFn: () => api.getAdminSupportRequest(id as string),
    enabled: Boolean(id),
  });
}

/** Invalidate every Support query (all list + detail variants) after a write. */
function useInvalidateSupport() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: supportKeys.all });
}

export function useUpdateSupportRequest() {
  const invalidate = useInvalidateSupport();
  return useMutation({
    mutationFn: ({
      id,
      patch,
    }: {
      id: string;
      patch: {
        status?: SupportRequestStatus;
        severity?: SupportRequestSeverity;
        resolution?: SupportRequestResolution;
        linked_issue?: string | null;
        admin_notes?: string | null;
      };
    }) => api.updateAdminSupportRequest(id, patch),
    onSuccess: invalidate,
  });
}

/** Put your own name on a request. One-shot: an owned request is not reassigned. */
export function useClaimSupportRequest() {
  const invalidate = useInvalidateSupport();
  return useMutation({
    mutationFn: ({ id }: { id: string }) => api.claimSupportRequest(id),
    onSuccess: invalidate,
  });
}

/** Send a one-shot, user-visible Reply (resolves the request). */
export function useReplyToSupportRequest() {
  const invalidate = useInvalidateSupport();
  return useMutation({
    mutationFn: ({ id, reply_body }: { id: string; reply_body: string }) =>
      api.replyToSupportRequest(id, reply_body),
    onSuccess: invalidate,
  });
}

/** Who is alerted about support requests. */
export function useSupportSettings() {
  return useQuery<SupportSettings>({
    queryKey: [...supportKeys.all, "settings"] as const,
    queryFn: () => api.getSupportSettings(),
  });
}

/** Change the support recipients. Superadmin only. */
export function useUpdateSupportSettings() {
  const invalidate = useInvalidateSupport();
  return useMutation({
    mutationFn: (patch: SupportSettingsUpdate) => api.updateSupportSettings(patch),
    onSuccess: invalidate,
  });
}

/** Superadmin-only correction of an already-sent Reply (no resend). */
export function useEditSupportReply() {
  const invalidate = useInvalidateSupport();
  return useMutation({
    mutationFn: ({ id, reply_body }: { id: string; reply_body: string }) =>
      api.editSupportReply(id, reply_body),
    onSuccess: invalidate,
  });
}
